import { useState } from "react";
import BulkSelectDevicesList from "../BulkSelectDevicesList";
import { ThreeColumnLayout } from "../../../layouts/ThreeColumnLayout/ThreeColumnLayout";
import { useSnackbar } from "../../../app/shared/snackbar-context/snackbar.context";
import BulkOperations from "../BulkOperations";
import { useTypedDispatch } from "../../../app/state";
import {
  bulkApprove,
  bulkApproveValidation,
  bulkOwnershipValidation,
  bulkReject,
  bulkRejectValidation,
  bulkSubmit,
  bulkSubmitValidation, bulkUpdateDeviceOperators, bulkUpdateDeviceOwners
} from "../../../store/deviceStore/deviceRequests";
import BulkInvalidDeviceList from "../BulkInvalidDeviceList";
import BulkOperationDetails from "../BulkOperationDetails";
import BulkConfirmation from "../BulkConfirmation";
import useNavigator from "../../../hooks/navigator.hook";
import { useShowModal } from "../../../app/shared/modal-context/modal.context";
import { ModalType } from "../../../app/shared/modal-context/constants";
import BulkLeftMenuSubmit from "../BulkLeftMenu/BulkLeftMenuSubmit";
import BulkLeftMenuApprove from "../BulkLeftMenu/BulkLeftMenuApprove";
import BulkLeftMenuReject from "../BulkLeftMenu/BulkLeftMenuReject";
import { BulkOperationSubTypeEnum } from "../BulkOperationSubType.enum";
import { BulkOperationTypeEnum } from "../BulkOperationType.enum";
import BulkUserFindAndSelect from "../BulkUserFindAndSelect";
import { sendCheckUser } from "../../../store/userStore/userRequests";
import { getUserInfo } from "../../../store/helperStore/helperRequests";

const WORKFLOW_DEFAULT_ACTION = BulkOperationSubTypeEnum.SUBMIT;
const OWNERSHIP_DEFAULT_ACTION = BulkOperationSubTypeEnum.OWNERS;

const BulkEditSteps = () => {
  const dispatch = useTypedDispatch();
  const { showModal } = useShowModal();
  const { navigateToSearchPage } = useNavigator();
  const [ currentStep, setStep ] = useState(0);
  const [ selectedDeviceIdList, setSelectedDevices ] = useState([] as number[]);
  const [ invalidDeviceIdList, setInvalidIdList ] = useState([] as number[]);
  const [ operationType, setOperationType ] = useState(BulkOperationTypeEnum.BULK_DEVICE_WORKFLOW);
  const [ currentOperation, setOperation ] = useState(WORKFLOW_DEFAULT_ACTION);
  const [ operationText, setOperationText ] = useState('');
  const [ userIdList, setUserIdList ] = useState([] as number[]);
  const { openSnackbar } = useSnackbar();

  const stepBack = () => {
    if (currentStep > 0) {
      if (currentStep === 3) {
        setStep(currentStep - 2);
      } else {
        setStep(currentStep - 1);
      }
    }
  }

  const validateSubmit = async () => {
    const action = await dispatch(bulkSubmitValidation({ deviceIdList: selectedDeviceIdList }))
    const response = action.payload as { deviceIdList: number[] };

    return response?.deviceIdList || [];
  }

  const validateApprove = async () => {
    const action = await dispatch(bulkApproveValidation({ deviceIdList: selectedDeviceIdList }))
    const response = action.payload as { deviceIdList: number[] };

    return response?.deviceIdList || [];
  }

  const validateReject = async () => {
    const action = await dispatch(bulkRejectValidation({ deviceIdList: selectedDeviceIdList }))
    const response = action.payload as { deviceIdList: number[] };

    return response?.deviceIdList || [];
  }

  const validateOwnership = async () => {
    const action = await dispatch(bulkOwnershipValidation({ deviceIdList: selectedDeviceIdList }))
    const response = action.payload as { deviceIdList: number[] };

    return response?.deviceIdList || [];
  }

  const validate = async () => {
    let invalidDeviceIdList: number[] = [];

    if (currentOperation === BulkOperationSubTypeEnum.SUBMIT) {
      invalidDeviceIdList = await validateSubmit();
    }

    if (currentOperation === BulkOperationSubTypeEnum.APPROVE) {
      invalidDeviceIdList = await validateApprove();
    }

    if (currentOperation === BulkOperationSubTypeEnum.REJECT) {
      invalidDeviceIdList = await validateReject();
    }

    if (currentOperation === BulkOperationSubTypeEnum.OWNERS || currentOperation === BulkOperationSubTypeEnum.OPERATORS) {
      invalidDeviceIdList = await validateOwnership();
    }

    setInvalidIdList(invalidDeviceIdList);
    return invalidDeviceIdList.length > 0;
  }

  const removeInvalidDevices = () => {
    return selectedDeviceIdList.filter((deviceId) => !invalidDeviceIdList.includes(deviceId));
  }

  const onInvalidDevices = (operation) => {
    openSnackbar({
      title: 'Validation error',
      message: `Failed to ${operation} due to re-validation. Please check invalid devices`,
      severity: 'error',
    });

    setInvalidIdList(invalidDeviceIdList);
    setStep(2);
  }

  const onSubmit = async () => {
    const action = await dispatch(bulkSubmit({ deviceIdList: selectedDeviceIdList, operationText }));
    const payload = action.payload as { invalidDeviceIdList: number[], tagModelId?: number };
    const { invalidDeviceIdList, tagModelId } = payload;

    if (invalidDeviceIdList.length) {
      onInvalidDevices('submit');
      return;
    }

    openSnackbar({
      title: 'Operation was successful',
      message: `${selectedDeviceIdList.length} devices were successfully submitted`,
      severity: 'success',
    });

    return tagModelId;
  }

  const onApprove = async () => {
    const action = await dispatch(bulkApprove({ deviceIdList: selectedDeviceIdList, operationText }));
    const payload = action.payload as { invalidDeviceIdList: number[], tagModelId?: number };
    const { invalidDeviceIdList, tagModelId } = payload;

    if (invalidDeviceIdList.length) {
      onInvalidDevices('approve');
      return;
    }

    openSnackbar({
      title: 'Operation was successful',
      message: `${selectedDeviceIdList.length} devices were successfully approved`,
      severity: 'success',
    });

    return tagModelId;
  }

  const onReject = async () => {
    const action = await dispatch(bulkReject({ deviceIdList: selectedDeviceIdList, operationText }));
    const payload = action.payload as { invalidDeviceIdList: number[], tagModelId?: number };
    const { invalidDeviceIdList, tagModelId } = payload;

    if (invalidDeviceIdList.length) {
      onInvalidDevices('reject');
      return;
    }

    openSnackbar({
      title: 'Operation was successful',
      message: `${selectedDeviceIdList.length} devices were successfully rejected`,
      severity: 'success',
    });

    return tagModelId;
  }

  const onOwnersUpdate = async () => {
    const action = await dispatch(bulkUpdateDeviceOwners({ deviceIdList: selectedDeviceIdList, ownerList: userIdList }));
    const payload = action.payload as { invalidDeviceIdList: number[], tagModelId?: number };
    const { invalidDeviceIdList, tagModelId } = payload;

    if (invalidDeviceIdList.length) {
      onInvalidDevices('update');
      return;
    }

    openSnackbar({
      title: 'Operation was successful',
      message: `${selectedDeviceIdList.length} devices were successfully updated`,
      severity: 'success',
    });

    return tagModelId;
  }

  const onOperatorsUpdate = async () => {
    const action = await dispatch(bulkUpdateDeviceOperators({ deviceIdList: selectedDeviceIdList, operatorList: userIdList }));
    const payload = action.payload as { invalidDeviceIdList: number[], tagModelId?: number };
    const { invalidDeviceIdList, tagModelId } = payload;

    if (invalidDeviceIdList.length) {
      onInvalidDevices('update');
      return;
    }

    openSnackbar({
      title: 'Operation was successful',
      message: `${selectedDeviceIdList.length} devices were successfully updated`,
      severity: 'success',
    });

    return tagModelId;
  }

  const onFinish = async () => {
    let tagModelId: number | undefined;

    switch (currentOperation) {
      case BulkOperationSubTypeEnum.SUBMIT:
        tagModelId = await onSubmit();
        break;
      case BulkOperationSubTypeEnum.APPROVE:
        tagModelId = await onApprove();
        break;
      case BulkOperationSubTypeEnum.REJECT:
        tagModelId = await onReject();
        break;
      case BulkOperationSubTypeEnum.OPERATORS:
        tagModelId = await onOperatorsUpdate();
        break;
      case BulkOperationSubTypeEnum.OWNERS:
        tagModelId = await onOwnersUpdate();
        break;
    }

    if (tagModelId) {
      navigateToSearchPage({ tags: [ tagModelId ] });
    }
  }

  const onNextStep = async () => {
    if (currentStep === 0) {
      if (selectedDeviceIdList.length === 0) {
        openSnackbar({
          title: 'Validation error',
          message: 'At least one device should be selected',
          severity: 'error',
        });

        return;
      }

      setStep(currentStep + 1);
    }

    if (currentStep === 1) {
      const validationResult = await validate();
      if (!validationResult) {
        setStep(currentStep + 2);
      } else {
        setStep(currentStep + 1);
      }
    }

    if (currentStep === 2) {
      const validationResult = await validate();

      if (!validationResult) {
        setStep(currentStep + 1);
        return;
      }

      showModal(ModalType.MODAL_GENERIC_CONFIRM, {
        title: 'Invalid devices',
        text: `${invalidDeviceIdList.length} Devices that are not valid for the operation will be removed from the list. Do you want to proceed?`,
        confirmButtonHandler: () => {
          const newDevicesList = removeInvalidDevices();

          if (newDevicesList.length === 0) {
            openSnackbar({
              title: 'Validation error',
              message: 'No devices will be left in the list after validation, should be at least one valid device',
              severity: 'error',
            });
          } else {
            setSelectedDevices(newDevicesList);
            setStep(currentStep + 1);
          }
        },
        confirmButtonText: 'Proceed',
      });

      return;
    }

    if (currentStep === 3) {
      setStep(currentStep + 1);
    }

    if (currentStep === 4) {
      await onFinish();
    }
  }

  const onSubOperationSet = (event) => {
    const type = event.target.value;
    setOperationType(type);
    const operation = type === BulkOperationTypeEnum.BULK_DEVICE_WORKFLOW ? WORKFLOW_DEFAULT_ACTION : OWNERSHIP_DEFAULT_ACTION;
    setOperation(operation);
  }

  const onOperationSet = (event) => {
    setOperation(event.target.value);
  }

  const onOperationTextUpdate = (event) => {
    setOperationText(event.target.value);
  }

  const onUserSelect = async (user) => {
    const action = await dispatch(sendCheckUser(user.email));
    const userId = action.payload;

    if (!userId || typeof userId !== 'number' || !isFinite(userId)) {
      return;
    }

    const newUserList = [ ...userIdList, userId ];

    await dispatch(getUserInfo(newUserList))

    setUserIdList(newUserList);
  }

  let centralPanelContent = (
    <BulkSelectDevicesList
      onSelect={setSelectedDevices}
      selectedDeviceIdList={selectedDeviceIdList}
    />
  );

  let leftMenu = (
    <BulkLeftMenuSubmit step={currentStep} onNextStep={onNextStep} onPreviousStep={stepBack} />
  );

  if (currentOperation === BulkOperationSubTypeEnum.APPROVE) {
    leftMenu = <BulkLeftMenuApprove step={currentStep} onNextStep={onNextStep} onPreviousStep={stepBack} />
  }

  if (currentOperation === BulkOperationSubTypeEnum.REJECT) {
    leftMenu = <BulkLeftMenuReject step={currentStep} onNextStep={onNextStep} onPreviousStep={stepBack} />
  }

  if (currentStep === 1) {
    centralPanelContent = (
      <BulkOperations
        selectedSubOperation={operationType}
        onSubOperationSet={onSubOperationSet}
        selectedOperation={currentOperation}
        onOperationSet={onOperationSet}
      />
    )
  }

  if (currentStep === 2) {
    centralPanelContent = (
      <BulkInvalidDeviceList
        deviceIdList={invalidDeviceIdList}
        actionName={currentOperation}
      />
    )
  }

  if (currentStep === 3) {
    if (currentOperation === BulkOperationSubTypeEnum.OWNERS || currentOperation === BulkOperationSubTypeEnum.OPERATORS) {
      centralPanelContent = <BulkUserFindAndSelect onUserSelect={onUserSelect} onUpdate={setUserIdList} userIdList={userIdList} />;
    } else {
      centralPanelContent = <BulkOperationDetails onUpdate={onOperationTextUpdate} operationText={operationText} />
    }
  }

  if (currentStep === 4) {
    let title = '';
    switch (currentOperation) {
      case BulkOperationSubTypeEnum.SUBMIT:
        title = `Confirmation: ${selectedDeviceIdList.length} devices will be submitted`;
        break;
      case BulkOperationSubTypeEnum.APPROVE:
        title = `Confirmation: ${selectedDeviceIdList.length} devices will be approved`;
        break;
      case BulkOperationSubTypeEnum.REJECT:
        title = `Confirmation: ${selectedDeviceIdList.length} devices will be rejected`;
        break;
      case BulkOperationSubTypeEnum.OWNERS:
        title = `Confirmation: in ${selectedDeviceIdList.length} devices owners list will be replaced`;
        break;
      case BulkOperationSubTypeEnum.OPERATORS:
        title = `Confirmation: in ${selectedDeviceIdList.length} devices operators list will be replaced`;
        break;
    }
    centralPanelContent = (
      <BulkConfirmation
        deviceIdList={selectedDeviceIdList}
        title={title}
      />
    )
  }

  return (
    <ThreeColumnLayout
      leftSideBar={leftMenu}
      centerMainPanel={centralPanelContent}
    />
  )
}

export default BulkEditSteps;